/**
 * Transporte **loopback HTTP** do ARGINK Bridge — o caminho preferencial da Qualificada.
 *
 * O Bridge instalado abre um servidor local em `127.0.0.1`, na ordem fixa 9987 → 9988 → 9989
 * (`serve`, D-013 do repositório `open-icp-bridge`). Esta página fala com ele por `fetch`,
 * sem extensão nenhuma. É isso que fecha a jornada do Gate B: quem instala o `.exe` volta
 * para a página já aberta e ela detecta sozinha — sem loja, sem reload, sem configuração.
 *
 * O MV3/Native Messaging (`window.openIcpBridge`) continua existindo como **fallback legado**
 * em `lib/bridge.ts`; não é requisito de nada.
 *
 * ## O que este módulo NÃO faz
 *
 * Não é uma segunda pilha de assinatura: ele só troca o carro pelo qual as MESMAS mensagens
 * v1 chegam ao MESMO `BridgeDispatcher`. Consentimento continua sendo diálogo nativo do host,
 * fora do alcance desta página, e toda conferência de resposta (hash do DER, eco de
 * `certificateId`, `signedDigestSha256`) segue em `lib/bridge.ts` — nada é acreditado aqui.
 *
 * ## Do lado do Bridge (a política que este cliente precisa respeitar)
 *
 * O servidor é fail-closed e só responde a quem cumpre tudo isto (D-014):
 * `Host` literalmente `127.0.0.1:<porta>`, origem na allowlist exata do `serve`, `POST`,
 * `Content-Type: application/json` e o header anti-CSRF `X-ARGINK-Bridge: 1`. Uma origem fora
 * da allowlist recebe 403 e NUNCA é ecoada em `Access-Control-Allow-Origin`.
 *
 * A origem canônica de produção é **`https://assinar.argink.com.br`**, gravada no instalador em tempo
 * de build (`-AllowedOrigins`, D-015 do `open-icp-bridge`). Em desenvolvimento, `--allow-dev-origins`
 * cobre as portas locais deste monorepo (5173/5174/5175/4173). Servir a UI de qualquer outra origem
 * exige acrescentá-la ao build do instalador — não há nada que esta página possa fazer para se
 * autorizar sozinha, e é essa a propriedade que se quer.
 */
import type {
  BridgeCertificateDescriptor,
  BridgeResponse,
  BridgeSignResult,
  BridgeVersionResult,
  OpenIcpBridgeApi,
} from './bridge';

/** A ordem é contratual: o Bridge tenta ligar nesta sequência e para na primeira livre. */
export const LOOPBACK_PORTS = [9987, 9988, 9989] as const;

/** Header anti-CSRF exigido pelo transporte — um `<form>` cross-site não consegue enviá-lo. */
const BRIDGE_HEADER = 'X-ARGINK-Bridge';

/**
 * Teto por porta na varredura. Curto de propósito: são três portas em loopback e isto roda
 * no caminho de detecção, que o usuário está esperando em tela.
 */
const PROBE_TIMEOUT_MS = 1_500;
/** `version`/`ping`: não passam pelo gate de consentimento do host. */
const QUICK_TIMEOUT_MS = 10_000;
/**
 * `list-certificates` e `get-certificate` passam pelo `DiscoveryGate` do host e abrem um diálogo
 * NATIVO que espera uma pessoa responder (teto de 90s, mais fila). Ver a nota longa em `bridge.ts`:
 * este teto existia como 10s e derrubava a listagem com a janela ainda aberta na tela do usuário.
 */
const CONSENT_TIMEOUT_MS = 180_000;
/** `sign` espera o humano responder ao diálogo nativo (e digitar PIN, em A3). */
const SIGN_TIMEOUT_MS = 180_000;

interface RequestEnvelope {
  protocolVersion: number;
  requestId: string;
  command: string;
  arguments?: unknown;
}

function newRequestId(): string {
  return `lb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Um POST ao Bridge. Sempre assenta: erro de rede, HTTP não-JSON e estouro de tempo viram
 * `ok:false` com código, para que quem chama tenha um único caminho de erro.
 *
 * `mode: 'cors'` é explícito porque é exatamente isso que está em jogo — o navegador só
 * entrega a resposta se o Bridge autorizar esta origem. Um 403 do servidor chega aqui como
 * corpo JSON legível; uma origem barrada no preflight chega como falha de rede, e ambos
 * significam a mesma coisa para o usuário: este Bridge não fala com esta página.
 */
async function post<T>(
  port: number,
  command: string,
  args: unknown,
  timeoutMs: number,
): Promise<BridgeResponse<T>> {
  const requestId = newRequestId();
  const envelope: RequestEnvelope = { protocolVersion: 1, requestId, command };
  if (args !== undefined) envelope.arguments = args;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`http://127.0.0.1:${port}/${command}`, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', [BRIDGE_HEADER]: '1' },
      body: JSON.stringify(envelope),
      signal: controller.signal,
    });
    const body = (await response.json()) as BridgeResponse<T>;
    return body;
  } catch (err) {
    const aborted = err instanceof Error && err.name === 'AbortError';
    return {
      protocolVersion: 1,
      requestId,
      ok: false,
      error: {
        code: aborted ? 'ERR_ACTION_TIMEOUT' : 'ERR_NATIVE_UNAVAILABLE',
        message: aborted
          ? 'o ARGINK Bridge não respondeu a tempo'
          : 'o ARGINK Bridge não está respondendo nesta máquina',
      },
    };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * A porta descoberta na última varredura bem-sucedida.
 *
 * Existe para não varrer três portas a cada chamada durante uma cerimônia. É invalidada assim
 * que uma chamada falha por indisponibilidade — o Bridge pode ter sido reiniciado noutra porta,
 * e insistir na porta velha transformaria isso num "Bridge sumiu" permanente.
 */
let cachedPort: number | null = null;

export function resetLoopbackPortCache(): void {
  cachedPort = null;
}

/** A porta em que o Bridge respondeu, ou `null` se nenhuma respondeu. Para logs e diagnóstico. */
export function loopbackPort(): number | null {
  return cachedPort;
}

/**
 * Procura o Bridge nas três portas, em ordem, e devolve a primeira que responder `version`
 * com sucesso. Não faz cache de ausência: se nada respondeu, a próxima chamada varre de novo —
 * é assim que a tela detecta sozinha uma instalação que acabou de terminar (Gate B).
 */
async function findPort(): Promise<number | null> {
  if (cachedPort !== null) {
    const probe = await post<BridgeVersionResult>(cachedPort, 'version', undefined, PROBE_TIMEOUT_MS);
    if (probe.ok) return cachedPort;
    cachedPort = null;
  }
  for (const port of LOOPBACK_PORTS) {
    const probe = await post<BridgeVersionResult>(port, 'version', undefined, PROBE_TIMEOUT_MS);
    if (probe.ok) {
      cachedPort = port;
      return port;
    }
  }
  return null;
}

/**
 * Devolve a API do Bridge sobre loopback, ou `null` se ele não estiver alcançável.
 *
 * A superfície é a MESMA que a extensão injeta, de propósito: `lib/bridge.ts` não precisa
 * saber por qual transporte falou, e a lógica de conferência continua num lugar só.
 */
export async function connectLoopbackBridge(): Promise<OpenIcpBridgeApi | null> {
  const port = await findPort();
  if (port === null) return null;

  /** Invalida a porta quando o Bridge deixa de responder, para a próxima varredura reachá-lo. */
  const track = <T>(response: BridgeResponse<T>): BridgeResponse<T> => {
    if (!response.ok && response.error?.code === 'ERR_NATIVE_UNAVAILABLE') cachedPort = null;
    return response;
  };

  return {
    protocolVersion: 1,
    transport: 'loopback',
    // Não há extensão neste caminho; o que existe é o programa instalado, e ele já respondeu
    // `version` acima. Reportar `true` aqui diz "o Bridge está alcançável", que é a pergunta
    // que `detectBridge` realmente faz.
    isExtensionPresent: () => true,
    // O transporte HTTP não publica `/ping` — os endpoints são `version`, `list-certificates`,
    // `get-certificate` e `sign`. `version` responde exatamente a mesma pergunta ("está vivo?")
    // e é o que o host usa como prova de vida.
    ping: () => post<unknown>(port, 'version', undefined, QUICK_TIMEOUT_MS).then(track),
    version: () => post<BridgeVersionResult>(port, 'version', undefined, QUICK_TIMEOUT_MS).then(track),
    listCertificates: (args) =>
      post<{ certificates?: BridgeCertificateDescriptor[] }>(
        port,
        'list-certificates',
        args ?? {},
        CONSENT_TIMEOUT_MS,
      ).then(track),
    getCertificate: (certificateId) =>
      post<{ certificateDer?: string }>(port, 'get-certificate', { certificateId }, CONSENT_TIMEOUT_MS).then(track),
    sign: (args, opts) =>
      post<BridgeSignResult>(port, 'sign', args, opts?.timeoutMs ?? SIGN_TIMEOUT_MS).then(track),
  };
}
